import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { WarningIcon } from '@shared/custom-icons';
import { ReactNode, useId } from 'react';
import { FilterChip } from '../Chips/FilterChip';
import { FilterField } from './FilterField';

interface BaseProps<T> {
  label: string;
  options: T[];
  placeholder: string;
  getOptionKey: (option: T) => string | number;
  getOptionLabel: (option: T) => string;
  renderOptionContent?: (option: T) => ReactNode;
  dividers?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

export interface SelectFilterProps<T> extends BaseProps<T> {
  value: T | null;
  onChange: (option: T | null) => void;
}

export interface MultiSelectFilterProps<T> extends BaseProps<T> {
  value: T[];
  onChange: (options: T[]) => void;
  getChipLabel?: (option: T) => string;
}

function filterAutocompleteParts<T>({
  getOptionKey,
  getOptionLabel,
  renderOptionContent,
  dividers,
  placeholder,
  isLoading,
  isError,
  errorMessage,
}: BaseProps<T>) {
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: unknown },
    option: T
  ) => {
    const { key, ...restProps } = props;
    return (
      <Stack
        key={String(key ?? getOptionKey(option))}
        {...restProps}
        component="li"
        alignItems="flex-start !important"
        sx={
          dividers
            ? {
                position: 'relative',
                '&:not(:last-of-type)::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }
            : undefined
        }
      >
        {renderOptionContent ? (
          renderOptionContent(option)
        ) : (
          <Typography variant="body2">{getOptionLabel(option)}</Typography>
        )}
      </Stack>
    );
  };

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      placeholder={placeholder}
      helperText={isError ? errorMessage : null}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading && <CircularProgress color="inherit" size={20} />}
              {isError && <WarningIcon />}
              {params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );

  return { renderOption, renderInput };
}

export function SelectFilter<T>(props: SelectFilterProps<T>) {
  const {
    label,
    options,
    value,
    onChange,
    getOptionKey,
    getOptionLabel,
    disabled,
    disabledReason,
    isLoading,
    isError,
  } = props;
  const { renderOption, renderInput } = filterAutocompleteParts(props);
  const inputId = useId();

  const autocomplete = (
    <Autocomplete
      id={inputId}
      size="small"
      options={options}
      value={value}
      disabled={disabled || isError}
      loading={isLoading}
      isOptionEqualToValue={(option, selected) => getOptionKey(option) === getOptionKey(selected)}
      getOptionLabel={getOptionLabel}
      onChange={(_, newValue) => onChange(newValue)}
      sx={{ width: '100%' }}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );

  return (
    <FilterField label={label} htmlFor={inputId}>
      {disabled && disabledReason ? (
        <Tooltip title={disabledReason}>
          <span>{autocomplete}</span>
        </Tooltip>
      ) : (
        autocomplete
      )}
    </FilterField>
  );
}

export function MultiSelectFilter<T>(props: MultiSelectFilterProps<T>) {
  const {
    label,
    options,
    value,
    onChange,
    getOptionKey,
    getOptionLabel,
    getChipLabel,
    disabled,
    disabledReason,
    isLoading,
  } = props;
  const { renderOption, renderInput } = filterAutocompleteParts(props);
  const inputId = useId();

  const autocomplete = (
    <Autocomplete
      id={inputId}
      size="small"
      multiple
      disableClearable
      filterSelectedOptions
      options={options}
      value={value}
      disabled={disabled}
      loading={isLoading}
      isOptionEqualToValue={(option, selected) => getOptionKey(option) === getOptionKey(selected)}
      getOptionLabel={getOptionLabel}
      onChange={(_, newValue) => onChange([...newValue])}
      sx={{ width: '100%' }}
      renderValue={(selected) =>
        selected.map((option) => (
          <FilterChip
            key={getOptionKey(option)}
            name={(getChipLabel ?? getOptionLabel)(option)}
            onDelete={() =>
              onChange(value.filter((item) => getOptionKey(item) !== getOptionKey(option)))
            }
          />
        ))
      }
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );

  return (
    <FilterField label={label} htmlFor={inputId}>
      {disabled && disabledReason ? (
        <Tooltip title={disabledReason}>
          <span>{autocomplete}</span>
        </Tooltip>
      ) : (
        autocomplete
      )}
    </FilterField>
  );
}
